import { useEffect, useState } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs } from "@volca/ui/components/ui/tabs";
import {
  canResolvePersistentEdit,
  type OverrideKind,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import type {
  IAiSpreadsheetCellLocation,
  IAiSpreadsheetCellOverride,
  IAiSpreadsheetManualRow,
  IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import {
  MANUAL_EDITS_TAB,
  ManualEditsTabs,
  visibleManualEditsTabs,
  type ManualEditsTab,
} from "./manual-edits-tabs";
import { ManualRowRow } from "./manual-row-row";
import { OverrideRow } from "./override-row";
import { SectionLabel } from "./shared";

interface OverrideSectionProps {
  label: string;
  showLabel?: boolean;
  rows: IAiSpreadsheetCellOverride[];
  kind: OverrideKind;
  businessId: string | null;
  runId: string;
  sheets: Pick<IAiSpreadsheetSheet, "id" | "editable">[] | undefined;
  onFocusEdit: (override: IAiSpreadsheetCellOverride) => void;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onWorkbookMutated?: () => void;
}

const OverrideSection = ({
  label,
  showLabel = true,
  rows,
  kind,
  businessId,
  runId,
  sheets,
  onFocusEdit,
  onGoToCell,
  onWorkbookMutated,
}: OverrideSectionProps) => {
  if (rows.length === 0) return null;

  return (
    <Stack className="w-full" align="start" gap={2}>
      {showLabel ? (
        <SectionLabel>
          {label} ({rows.length})
        </SectionLabel>
      ) : null}
      {rows.map((override) => (
        <OverrideRow
          key={override.id}
          override={override}
          businessId={businessId}
          runId={runId}
          kind={kind}
          canResolve={
            sheets ? canResolvePersistentEdit(override, sheets) : null
          }
          onFocus={() => onFocusEdit(override)}
          onGoToCell={onGoToCell}
          onWorkbookMutated={onWorkbookMutated}
        />
      ))}
    </Stack>
  );
};

interface ManualEditsListProps {
  applied: IAiSpreadsheetCellOverride[];
  needsReview: IAiSpreadsheetCellOverride[];
  dropped: IAiSpreadsheetCellOverride[];
  manualRows: IAiSpreadsheetManualRow[];
  businessId: string | null;
  runId: string;
  sheets: IAiSpreadsheetSheet[] | undefined;
  onFocusEdit: (override: IAiSpreadsheetCellOverride) => void;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onWorkbookMutated?: () => void;
}

export const ManualEditsList = ({
  applied,
  needsReview,
  dropped,
  manualRows,
  businessId,
  runId,
  sheets,
  onFocusEdit,
  onGoToCell,
  onWorkbookMutated,
}: ManualEditsListProps) => {
  const tabCounts = {
    all:
      applied.length + needsReview.length + dropped.length + manualRows.length,
    review: needsReview.length + dropped.length,
    applied: applied.length,
    added: manualRows.length,
  };
  const tabs = visibleManualEditsTabs(tabCounts);
  const [selectedTab, setSelectedTab] = useState<ManualEditsTab>(
    MANUAL_EDITS_TAB.all,
  );
  const selectedTabIsVisible = tabs.includes(selectedTab);
  const activeTab = selectedTabIsVisible ? selectedTab : MANUAL_EDITS_TAB.all;

  useEffect(() => {
    if (!selectedTabIsVisible) setSelectedTab(MANUAL_EDITS_TAB.all);
  }, [selectedTabIsVisible]);

  const showsTab = (tab: ManualEditsTab) =>
    activeTab === MANUAL_EDITS_TAB.all || activeTab === tab;
  const showsSectionLabels = activeTab !== MANUAL_EDITS_TAB.applied;
  const sectionProps = {
    businessId,
    runId,
    sheets,
    onFocusEdit,
    onGoToCell,
    onWorkbookMutated,
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => setSelectedTab(value as ManualEditsTab)}
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <ManualEditsTabs tabs={tabs} counts={tabCounts} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Stack
          gap={6}
          align="stretch"
          className="max-w-(--simple-sidebar-width,436px) px-5 py-5"
          data-manual-edits-scroll-content
        >
          <OverrideSection
            {...sectionProps}
            label="Needs review"
            rows={showsTab(MANUAL_EDITS_TAB.review) ? needsReview : []}
            kind="needs_review"
            showLabel={showsSectionLabels}
          />
          <OverrideSection
            {...sectionProps}
            label="Dropped"
            rows={showsTab(MANUAL_EDITS_TAB.review) ? dropped : []}
            kind="dropped"
            showLabel={showsSectionLabels}
          />
          {showsTab(MANUAL_EDITS_TAB.added) && manualRows.length > 0 ? (
            <Stack className="w-full" align="start" gap={2}>
              {activeTab === MANUAL_EDITS_TAB.all ? (
                <SectionLabel>Added rows ({manualRows.length})</SectionLabel>
              ) : null}
              {manualRows.map((row) => (
                <ManualRowRow
                  key={row.id}
                  row={row}
                  businessId={businessId}
                  runId={runId}
                  sheets={sheets}
                  onGoToCell={onGoToCell}
                  onWorkbookMutated={onWorkbookMutated}
                />
              ))}
            </Stack>
          ) : null}
          <OverrideSection
            {...sectionProps}
            label="Applied"
            rows={showsTab(MANUAL_EDITS_TAB.applied) ? applied : []}
            kind="applied"
            showLabel={showsSectionLabels}
          />
        </Stack>
      </div>
    </Tabs>
  );
};
